import { PluginKey, LogModule } from '@/data/config';
import { loggerInfo } from '@/modules/logger';
import { isAndroid, isIOS } from '@/modules/detector';
import loadJS from '@/modules/loadjs';
import { isNumber } from '@/modules/validate';

import { blobToFile, dataURItoBlob } from './common';

// interface
import { Images } from '@/interface';
import { getPluginUrl } from '../config';

/**
 * 获取ios版本号
 */
const getIOSVersion = () => {
  if (!isIOS()) return '';
  const ua = navigator.userAgent;
  const i = ua.indexOf(' OS ') + 4;
  return ua.substring(i, ua.indexOf(' ', i));
};
/**
 * 比较ios版本号
 * @param v1 版本1
 * @param v2 版本2
 * @returns 1， -1，0
 */
const iosVersionCompare = (v1: string, v2: string): number => {
  const v1Arr: Array<string> = v1.split('_');
  const v2Arr: Array<string> = v2.split('_');
  if (v1Arr.length > v2Arr.length) {
    const len = v1Arr.length - v2Arr.length;
    for (let i = 0; i < len; i++) {
      v2Arr.push('0');
    }
  }

  if (v1Arr.length < v2Arr.length) {
    const len = v2Arr.length - v1Arr.length;
    for (let i = 0; i < len; i++) {
      v1Arr.push('0');
    }
  }

  for (let i = 0; i < v1Arr.length; i++) {
    const item1 = +v1Arr[i];
    const item2 = +v2Arr[i];
    if (item1 === item2) {
      continue;
    }
    if (item1 < item2) {
      return -1;
    }
    if (item1 > item2) {
      return 1;
    }
  }

  return 0;
};

/**
 * ios是否需要旋转
 */
function isIOSNeedRotate() {
  if (!isIOS()) return false;
  const iOSVersion = getIOSVersion();
  if (!iOSVersion) {
    return false;
  }

  if (iosVersionCompare(iOSVersion, '13_4') < 0) {
    loggerInfo(['check iOS need rotate: ', true], LogModule.Image);
    return true;
  }

  loggerInfo(['check iOS need rotate: ', false], LogModule.Image);
  return false;
}

function isNeedRotateImg(file: File, exifUrl?: string): Promise<number | undefined> {
  return new Promise(reslove => {
    loadJS(exifUrl ?? getPluginUrl().EXIF, PluginKey.EXIF, 'exif')
      .then(EXIF => {
        if (!EXIF) {
          reslove(undefined);
        }
        EXIF.getData(file, function (this: unknown) {
          const orientation: number = EXIF.getTag(this, 'Orientation');
          reslove(orientation);
        });
      })
      .catch(e => {
        console.error(e);
        reslove(undefined);
      });
  });
}

function arrayBufferToStr(data: ArrayBuffer): string {
  // eslint-disable-next-line
  return String.fromCharCode.apply(null, new Uint8Array(data) as any);
}

function fileReader(file: File): Promise<string | ArrayBuffer> {
  return new Promise((reslove, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      if (!event || !event.target) return;

      const result = event.target.result;

      if (result) {
        reslove(result);
        return;
      }
      throw new Error('reader error');
    };
    reader.onerror = () => {
      reject(new Error('reader file error'));
    };
  });
}

function createImgElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      loggerInfo('createImgElement：onload', LogModule.Image);

      resolve(image);
    };

    image.onerror = () => {
      loggerInfo('createImgElement：onerror', LogModule.Image);
      reject(new Error('create image error'));
    };

    image.src = src;

    // IOS 部分机型。上传图片没有onload, onerror 回调，不要阻塞上传流程
    setTimeout(() => {
      loggerInfo('createImgElement：超时处理', LogModule.Image);
      resolve(image);
    }, 800);
  });
}

// 使用canvas对大图片进行压缩
// 1. 图片的大小，如果图片的大小超过两百万像素， 无法绘制在canvas
// 2. canvas的大小有限制 五百万像素（即宽高乘积）
// 3. canvas的toDataURL是只能压缩jpg的, 拍照圖片都是jpg
class imgCompress implements Images.IImgCompress {
  public maxPixels = 4000000; // 限制壓縮最大比例
  public level; // 压缩质量
  public compressMinSize = 100 * 1024; // 大於100KB才壓縮
  public convertMaxSize = 300 * 1024;
  public compressType = ''; // 压缩类型
  public isCycle = true; // 判断是否循环压缩
  public backgroundColor = '#fff'; // 默认背景是白色
  public exifUrl = ''; // exif链接
  constructor(options: {
    maxPixels?: number;
    level?: number;
    compressMinSize?: number;
    convertMaxSize?: number;
    compressType?: string;
    isCycle?: boolean;
    backgroundColor?: string;
    exifUrl?: string;
  }) {
    this.maxPixels = options?.maxPixels ?? this.maxPixels;
    this.level = options?.level ?? this.maxPixels;
    this.compressMinSize = options?.compressMinSize ?? this.compressMinSize;
    this.convertMaxSize = options?.convertMaxSize ?? this.convertMaxSize;
    this.compressType = options?.compressType ?? this.compressType;
    this.isCycle = options?.isCycle ?? this.isCycle;
    this.backgroundColor = options?.backgroundColor ?? this.backgroundColor;
    this.exifUrl = options?.exifUrl ?? this.exifUrl;
  }

  public setMaxPixels(maxPixels: number): void {
    if (!maxPixels) return;
    if (maxPixels > 4000000) {
      throw new Error('不能超过400万像素');
    }
    this.maxPixels = maxPixels;
  }

  public setLevel(level: number): void {
    if (!level) return;
    this.level = level;
  }

  public setCompressMinSize(compressMinSize: number): void {
    if (!compressMinSize) return;
    this.compressMinSize = compressMinSize;
  }

  public setConvertMaxSize(convertMaxSize: number): void {
    if (!convertMaxSize) return;
    this.compressMinSize = convertMaxSize;
  }

  public setCompressType(type: string): void {
    this.compressType = type;
  }

  public setIsCycle(isCycle: boolean): void {
    this.isCycle = isCycle;
  }

  public setBackgroundColor(color: string): void {
    this.backgroundColor = color;
  }

  public setExifUrl(url: string): void {
    this.exifUrl = url;
  }
  /**
   * 获取图片的宽高
   */
  public getImgRect(
    this: Images.IImgCompress & { maxPixels: number },
    img: HTMLImageElement
  ): {
    width: number;
    height: number;
    ratio: number;
  } {
    let width = img.width;
    let height = img.height;

    // 如果图片大于四百万像素，计算压缩比并将大小压至400万以下
    let ratio;
    if ((ratio = (width * height) / this.maxPixels) > 1) {
      ratio = Math.sqrt(ratio);
      width /= ratio;
      height /= ratio;
    } else {
      ratio = 1;
    }
    loggerInfo(['utils first compress width height ', width, height, ratio], LogModule.Image);
    // 如果長寬比 過大 1:2.5 的情況下，最小的一邊爲 500 pixel
    if (ratio < 1) {
      const longType = width >= height ? 1 : 2;
      const aspectRatio = width >= height ? width / height : height / width;
      if (aspectRatio > 2) {
        if (longType === 1 && width < 800) {
          const tmpRatio = 800 / width;
          ratio = ratio * tmpRatio;
          width = 800;
          height = height * tmpRatio;
        } else if (longType === 2 && height < 800) {
          const tmpRatio = 800 / height;
          ratio = ratio * tmpRatio;
          height = 800;
          width = width * tmpRatio;
        }
      }
      loggerInfo(['utils second compress width height ', width, height, ratio], LogModule.Image);
    }
    return {
      width,
      height,
      ratio,
    };
  }

  public compressImg(this: Images.IImgCompress, img: HTMLImageElement): string {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const tCanvas = document.createElement('canvas');
    const tctx = tCanvas.getContext('2d');
    if (!tctx) return '';

    const initSize = img.src.length;
    loggerInfo(['压缩前: ', initSize, img.width, img.height], LogModule.Image);

    // 获取图片宽度高度
    const { width, height, ratio } = this.getImgRect(img);

    loggerInfo(['utils', width, height, ratio], LogModule.Image);

    canvas.width = width;
    canvas.height = height;
    // 铺底色
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    loggerInfo(['压缩前: ', width], LogModule.Image);
    loggerInfo(['压缩前: ', height], LogModule.Image);
    loggerInfo(['压缩前: ', this.maxPixels], LogModule.Image);
    loggerInfo(['压缩前: ', (width * height) / this.maxPixels], LogModule.Image);
    // 如果图片像素大于400万则使用瓦片绘制，不能超过400万
    let count;

    count = String((width * height) / this.maxPixels).match(/\d+\.\d{3}/);
    count = count ? Number(count) : 0;
    if (isNumber(count) && count > 1) {
      console.log(['压缩前: ', '分瓦片压缩', count], LogModule.Image);
      // 计算要分成多少块瓦片
      count = ~~(Math.sqrt(count) + 1);
      // 计算每块瓦片的宽和高
      const nw = ~~(width / count);
      const nh = ~~(height / count);
      tCanvas.width = nw;
      tCanvas.height = nh;

      for (let i = 0; i < count; i++) {
        for (let j = 0; j < count; j++) {
          tctx.drawImage(img, i * nw * ratio, j * nh * ratio, nw * ratio, nh * ratio, 0, 0, nw, nh);
          ctx.drawImage(tCanvas, i * nw, j * nh, nw, nh);
        }
      }
    } else {
      ctx.drawImage(img, 0, 0, width, height);
    }

    // 进行最小压缩
    const mimeString = img.src.split(',')[0].split(':')[1].split(';')[0];
    const ndata = canvas.toDataURL(this.compressType || mimeString, this.level);

    loggerInfo(['压缩后: ', ndata.length], LogModule.Image);
    loggerInfo(
      ['压缩率: ', ~~((100 * (initSize - ndata.length)) / initSize) + '%'],
      LogModule.Image
    );
    // 清除画布
    tCanvas.width = tCanvas.height = canvas.width = canvas.height = 0;

    loggerInfo('这里', LogModule.Image);
    return ndata;
  }

  public rotateImg(img: HTMLImageElement, orientation: number) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    const imgW: number = img.width;
    const imgH: number = img.height;
    ctx.fillStyle = this.backgroundColor;
    canvas.width = imgW;
    canvas.height = imgH;

    // 图片角度不正确
    switch (orientation) {
      case 6:
        // 旋转90度
        canvas.width = imgH;
        canvas.height = imgW;
        ctx.rotate(Math.PI / 2);
        // (0,-imgH) 从旋转原理图那里获得的起始点
        ctx.drawImage(img, 0, -imgH, imgW, imgH);
        break;
      case 3:
        // 旋转180度
        ctx.rotate(Math.PI);
        ctx.drawImage(img, -imgW, -imgH, imgW, imgH);
        break;
      case 8:
        // 旋转-90度
        canvas.width = imgH;
        canvas.height = imgW;
        ctx.rotate((3 * Math.PI) / 2);
        ctx.drawImage(img, -imgW, 0, imgW, imgH);
        break;
    }

    const mimeString = img.src.split(',')[0].split(':')[1].split(';')[0];
    const base64Data = canvas.toDataURL(mimeString, 1);
    return base64Data;
  }

  /**
   * 压缩大于100K的图片
   */
  public async compressIfLargerThan(
    this: Images.IImgCompress,
    file: File,
    rename?: string,
    isNewFile?: boolean
  ) {
    // 判断是否需要旋转
    // eslint-disable-next-line
    let orientation: any = undefined;
    let isNeedRotate = false;

    if (isIOSNeedRotate() || isAndroid()) {
      orientation = await isNeedRotateImg(file, this.exifUrl);
      loggerInfo(['旋转角度 orientation', orientation], LogModule.Image);
      isNeedRotate = orientation && orientation !== 1; // 判断是否需要旋转
    }

    console.log(file);
    const result: string | ArrayBuffer = await fileReader(file);
    const length = result instanceof ArrayBuffer ? result.byteLength : result.length;

    // 小于100kb 不压缩
    if (!length || length <= this.compressMinSize) {
      return file;
    }

    loggerInfo('创建图片 createImgElement', LogModule.Image);

    const fileImg = await createImgElement(
      result instanceof ArrayBuffer ? arrayBufferToStr(result) : result
    );

    loggerInfo('创建结束 createImgElement', LogModule.Image);

    let rotateImg;
    if (isNeedRotate) {
      const base64DataRotated = this.rotateImg(fileImg, orientation);
      rotateImg = await createImgElement(base64DataRotated);

      loggerInfo('旋转图片结束 createImgElement', LogModule.Image);
    }

    const imgEL = isNeedRotate && rotateImg ? rotateImg : fileImg;

    loggerInfo(['开始压缩图片 compressImg', isNeedRotate, imgEL.complete], LogModule.Image);

    let base64Data, blob;
    // eslint-disable-next-line prefer-const
    base64Data = this.compressImg(imgEL);

    loggerInfo('压缩图片结束 compressImg', LogModule.Image);

    if (!base64Data) {
      loggerInfo(['compressImg error:', length], LogModule.Image);

      throw new Error(String(length));
    }

    loggerInfo('base64 转 blob 开始', LogModule.Image);

    // eslint-disable-next-line prefer-const
    blob = dataURItoBlob(base64Data);

    loggerInfo('base64 转 blob 结束', LogModule.Image);

    if (this.isCycle) {
      loggerInfo('循环压缩开始', LogModule.Image);
      while (blob.size >= this.convertMaxSize) {
        this.setMaxPixels(this.maxPixels * 0.85);
        base64Data = this.compressImg(isNeedRotate && rotateImg ? rotateImg : fileImg);
        if (!base64Data) {
          loggerInfo(['compressImg error:', length], LogModule.Image);
          throw new Error(String(length));
        }
        blob = dataURItoBlob(base64Data);
      }

      loggerInfo('循环压缩结束', LogModule.Image);
    }

    // 判断是否需要修改文件名
    const type = blob.type.replace(/image\//, '');
    const name =
      rename ??
      file?.name?.replace(/\.(png|jpg|gif|jpeg|webp)$/gi, `.${type}`) ??
      `${+new Date()}.${type}`;

    return blobToFile(blob, name, isNewFile);
  }
}

// 单例模式
let instance: Images.IImgCompress;
export function imgCompressInstance(options: {
  maxPixels?: number;
  level?: number;
  compressMinSize?: number;
  convertMaxSize?: number;
  compressType?: string;
  isCycle?: boolean;
  backgroundColor?: string;
  exifUrl?: string;
}): Images.IImgCompress {
  const _opt = {
    maxPixels: 4000000, // 限制壓縮最大比例
    level: 0.92, // 压缩质量
    compressMinSize: 100 * 1024, // 大於100KB才壓縮
    convertMaxSize: 300 * 1024,
    compressType: '', // 压缩类型
    isCycle: true, // 判断是否循环压缩
    backgroundColor: '#fff',
    exifUrl: getPluginUrl().EXIF,
    ...options,
  };
  if (!instance) {
    instance = new imgCompress(_opt);
  } else {
    instance.setMaxPixels(_opt.maxPixels);
    instance.setLevel(_opt.level);
    instance.setCompressMinSize(_opt.compressMinSize);
    instance.setConvertMaxSize(_opt.convertMaxSize);
    instance.setCompressType(_opt.compressType);
    instance.setIsCycle(_opt.isCycle);
    instance.setBackgroundColor(_opt.backgroundColor);
    instance.setExifUrl(_opt.exifUrl);
  }
  return instance;
}
